import type { DraftPart, PartProblem } from "@/lib/assetContent";
import type { NamedDocument } from "@/lib/namedParts";
import {
  declaredStepRoles,
  parseScreeningDocument,
  referencedStepIds,
  screeningStepIds,
  serializeScreeningDocument,
  type ScreeningDiagnosisDocument,
  type ScreeningDocumentSchema,
  type ScreeningStepsDocument,
} from "@/lib/screeningDocuments";
import {
  contextForLanguage,
  type ScreeningRuleProjection,
} from "@/lib/screeningSemantics";
import { UnavailableState } from "@/ui/states";

import { ScreeningDiagnosisForm } from "./ScreeningDiagnosisForm";
import { ScreeningStepsForm } from "./ScreeningStepsForm";

/**
 * The typed editor for one screening role, across the languages it is stored in
 * (#912).
 *
 * `NamedDocumentEditor`'s shape, for the same reasons and with one substitution:
 * the read-only explanation and the set-level problems are rendered once for the
 * row rather than once per language, and each stored document gets its own form.
 * The two are separate components rather than one with a mode, because the
 * document a prompt role holds is a string and the document these roles hold is
 * a structure — a single component would be a `kind` branch wrapping two
 * unrelated bodies, which is what `PathwayAssetPage` already does one level up
 * and does not need twice.
 *
 * A DOCUMENT THAT DOES NOT PARSE IS EXPLAINED, NOT CRASHED ON. Versions are
 * immutable, undeletable and older than this schema, so a stored document that
 * is not an object is a state this surface can genuinely meet. It says so and
 * leaves the content alone; the version route still serves the exact stored
 * bytes.
 */
export function ScreeningDocumentEditor({
  documents,
  schema,
  parts,
  publishedStepIdHighWater,
  loadedParts,
  screeningRules,
  screeningRulesPending,
  screeningRulesFailed,
  onRetryScreeningRules,
  readOnly,
  readOnlyReason,
  problems,
  onChange,
}: {
  documents: readonly NamedDocument[];
  schema: ScreeningDocumentSchema;
  /**
   * The whole draft, for the diagnosis form's step vocabulary.
   *
   * The sibling `screening_steps` and `screening_schema` documents are what the
   * service checks a `stepN` reference against, and they live in other parts of
   * the same draft — so the check sees the steps as the clinician has them NOW,
   * including edits not yet saved, rather than the version they opened.
   */
  parts: readonly DraftPart[];
  /**
   * The greatest step id this asset has published, or `null` when the version
   * history has not been read. Passed through to the steps form, which refuses
   * Add rather than allocating an id it cannot confirm is retired.
   */
  publishedStepIdHighWater: number | null | undefined;
  /** The pinned baseline, whose ids remain retired during cache refreshes. */
  loadedParts: readonly DraftPart[];
  /**
   * The service's own composition of this version's diagnosis rules — the
   * classification criteria and base canon's document at the same coordinate.
   *
   * `null` while the read has not answered. The diagnosis form distinguishes
   * "not read yet" from "read, and could not be resolved", because a form that
   * showed the second as the first would look like it was loading forever.
   */
  screeningRules: ScreeningRuleProjection | null;
  screeningRulesPending: boolean;
  /**
   * The projection READ failed — offline, or refused — as distinct from the
   * service ANSWERING that a half could not be resolved.
   *
   * Kept apart all the way down because the two need different words and only
   * one of them can be retried. A transport failure rendered as a content
   * refusal reads as "no reason was given", which is the least useful true
   * sentence this surface could produce.
   */
  screeningRulesFailed: boolean;
  /** Absent when the failure is terminal and retrying would only repeat it. */
  onRetryScreeningRules?: () => void;
  readOnly: boolean;
  readOnlyReason: string | null;
  problems: readonly PartProblem[];
  onChange: (documentId: string, text: string) => void;
}) {
  const listProblems = problems.filter((problem) => problem.index === null);

  return (
    <div className="space-y-4">
      {readOnly && readOnlyReason ? (
        <p className="rounded-md border border-border bg-muted px-3 py-2 text-sm text-muted-foreground">
          {readOnlyReason}
        </p>
      ) : null}

      {listProblems.length > 0 ? (
        // The wrapper is the live region, never each item: a `role` on an `<li>`
        // replaces its implicit `listitem` and leaves the `<ul>` holding
        // children that are not list items. `NamedDocumentEditor` states this
        // in full; the two are one idiom rather than two judgements.
        <div role="alert">
          <ul className="space-y-1">
            {listProblems.map((problem, position) => (
              <li
                key={`${String(position)}:${String(problem.index)}:${problem.message}`}
                className="text-sm text-destructive"
              >
                {problem.message}
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      <div className="space-y-6">
        {documents.map((document) => (
          <ScreeningDocumentField
            key={document.id}
            document={document}
            schema={schema}
            parts={parts}
            publishedStepIdHighWater={publishedStepIdHighWater}
            loadedParts={loadedParts}
            screeningRules={screeningRules}
            screeningRulesPending={screeningRulesPending}
            screeningRulesFailed={screeningRulesFailed}
            {...(onRetryScreeningRules
              ? { onRetryScreeningRules }
              : {})}
            showLanguage={documents.length > 1}
            readOnly={readOnly}
            onChange={(text) => onChange(document.id, text)}
          />
        ))}
      </div>
    </div>
  );
}

function ScreeningDocumentField({
  document,
  schema,
  parts,
  publishedStepIdHighWater,
  loadedParts,
  screeningRules,
  screeningRulesPending,
  screeningRulesFailed,
  onRetryScreeningRules,
  showLanguage,
  readOnly,
  onChange,
}: {
  document: NamedDocument;
  schema: ScreeningDocumentSchema;
  parts: readonly DraftPart[];
  publishedStepIdHighWater: number | null | undefined;
  loadedParts: readonly DraftPart[];
  screeningRules: ScreeningRuleProjection | null;
  screeningRulesPending: boolean;
  screeningRulesFailed: boolean;
  onRetryScreeningRules?: () => void;
  showLanguage: boolean;
  readOnly: boolean;
  onChange: (text: string) => void;
}) {
  const parsed = parseScreeningDocument(document.text, schema);

  const body =
    parsed === null ? (
      <UnavailableState
        title="This document is not in the shape its schema describes"
        description="It is stored as something other than a set of fields, so the named controls cannot show it. Its content is unchanged and still part of this asset."
      />
    ) : schema === "screening_steps" ? (
      <ScreeningStepsForm
        document={parsed as ScreeningStepsDocument}
        reservedStepIds={
          // THE DRAFT'S LIVE REFERENCES AND THE ASSET'S PUBLISHED HISTORY, as
          // one mark. Either alone leaves a hole: the draft cannot know an id a
          // previous version retired, and the history cannot know a rule the
          // clinician has just written.
          //
          // ONLY `undefined` propagates as "not known" and refuses Add. A
          // `null` high-water is the service ANSWERING that no stored version
          // claims an id — the ordinary state of a Pathway with no published
          // screening steps — and the mark is then just what this draft holds.
          publishedStepIdHighWater === undefined
            ? null
            : new Set([
                ...(publishedStepIdHighWater === null
                  ? []
                  : [publishedStepIdHighWater]),
                ...screeningStepIds(loadedParts),
                ...referencedStepIds(parts),
              ])
        }
        readOnly={readOnly}
        onChange={(next) => onChange(serializeScreeningDocument(next))}
      />
    ) : (
      <ScreeningDiagnosisForm
        document={parsed as ScreeningDiagnosisDocument}
        declaredStepRoles={declaredStepRoles(parts, document.language)}
        // THE PROJECTION'S ROW FOR THIS DOCUMENT'S OWN LANGUAGE. A Hebrew
        // diagnosis resolves against the Hebrew classification schema and the
        // Hebrew base document; describing one language's rules with another's
        // criteria is the mistake a single shared context would make.
        context={contextForLanguage(screeningRules, document.language)}
        contextPending={screeningRulesPending}
        contextFailed={screeningRulesFailed}
        {...(onRetryScreeningRules ? { onRetryContext: onRetryScreeningRules } : {})}
        readOnly={readOnly}
        onChange={(next) => onChange(serializeScreeningDocument(next))}
      />
    );

  if (!showLanguage) {
    return body;
  }
  return (
    // A read-only fact, shown only where the role is genuinely stored in more
    // than one language and two forms would otherwise be indistinguishable.
    // Never a control that authors a prefix.
    <section className="space-y-2">
      <h3 className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
        {document.language ?? "All languages"}
      </h3>
      {body}
    </section>
  );
}
